/**
 * The workspace — approved 30 September 2026 (Q13).
 *
 * "Current and previous dated lists, saved reports, next delivery time."
 * It opens populated; §4 is explicit that it must never be an empty
 * dashboard.
 *
 * Q17 — after the access period this page is still the whole history, and it
 * shows the upgrade prompt rather than an apology. "Preserve historical
 * access; do not create a dead end."
 */

import React from 'react';
import { copyFor, SHARED } from './audienceCopy';

function formatDay(value) {
  if (!value) return '';
  // Day and month only. A time would imply the list was assembled at that
  // instant, and the run that produced it may have finished hours earlier.
  return new Date(value).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

function formatDelivery(value) {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    weekday: 'long', hour: '2-digit', minute: '2-digit',
  });
}

export default function WorkspaceStep({
  audience, workspace, onOpenList, onOpenReport, onUpgrade,
}) {
  const copy = copyFor(audience);
  const { lists = [], reports = [], nextDeliveryAt, access = {} } = workspace || {};
  const expired = access.enrolled && access.expired;

  return (
    <>
      <p className="cmp-eyebrow">Your workspace</p>
      <h1 className="cmp-title">{copy.workspaceTitle}</h1>

      {/* Q17 — the prompt, not an error, and not instead of the content. */}
      {expired && (
        <div className="cmp-notice">
          <div>
            <strong className="cmp-section-title">{SHARED.expiredTitle}</strong>
            <p className="cmp-faq-answer">{SHARED.expiredBody}</p>
            {onUpgrade && (
              <button type="button" className="cmp-btn cmp-btn--primary" onClick={onUpgrade}>
                {SHARED.upgradeAction}
              </button>
            )}
          </div>
        </div>
      )}

      {!expired && nextDeliveryAt && (
        <p className="cmp-lede">Your next list arrives {formatDelivery(nextDeliveryAt)}.</p>
      )}

      <section>
        <h2 className="cmp-section-title">Your lists</h2>
        {lists.length === 0 ? (
          <p className="cmp-help">Your first list will appear here.</p>
        ) : (
          <div className="cmp-list">
            {lists.map((list, index) => (
              <article className="cmp-opportunity" key={list.listId}>
                <div className="cmp-opportunity-index">{String(index + 1).padStart(2, '0')}</div>
                <div>
                  <h3 className="cmp-opportunity-headline">{formatDay(list.date)}</h3>
                  {list.count != null && (
                    <p className="cmp-opportunity-reason">
                      {list.count} {list.count === 1 ? 'opportunity' : 'opportunities'}
                    </p>
                  )}
                </div>
                {/* Q13 — "Reports can be generated from older lists while
                    access/allowance remain", so every list opens, not just
                    the newest. */}
                <button
                  type="button"
                  className="cmp-btn cmp-btn--secondary"
                  onClick={() => onOpenList(list.listId)}
                >
                  Open
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section style={{ marginTop: 32 }}>
        <h2 className="cmp-section-title">Your research</h2>
        {reports.length === 0 ? (
          <p className="cmp-help">Research you create will be saved here.</p>
        ) : (
          <div className="cmp-list">
            {reports.map((report) => (
              <article className="cmp-opportunity" key={report.reportId}>
                <div className="cmp-opportunity-index" />
                <div>
                  <h3 className="cmp-opportunity-headline">{report.storyTitle || 'Research'}</h3>
                  <p className="cmp-opportunity-reason">
                    {report.status === 'ready'
                      ? formatDay(report.readyAt)
                      : report.status === 'failed'
                        ? 'Did not finish'
                        : 'Preparing'}
                  </p>
                </div>
                <button
                  type="button"
                  className="cmp-btn cmp-btn--secondary"
                  disabled={report.status !== 'ready'}
                  onClick={() => onOpenReport(report.reportId)}
                >
                  Open
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* §4 — the workspace says plainly that this is one part of Alchemiq. */}
      <p className="cmp-help" style={{ marginTop: 32 }}>{copy.workspaceWiderPlatform}</p>
    </>
  );
}
